import React, { useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import Typography from '../Text/Typography';
import Icon from '../Icon/Icon';
import { useAuth } from '../../contexts/AuthContext';
import { useRoutineSetupChecklist } from '../../contexts/RoutineSetupChecklistContext';
import './RoutineSetupChecklist.css';

const STEPS = [
  {
    stepIndex: 1,
    key: 'hasCabinetProduct',
    label: 'Add products to Skincare Cabinet',
    helper: 'Add at least one product to get started',
    path: '/skincarecabinet',
    openAddProduct: true,
  },
  {
    stepIndex: 2,
    key: 'hasSuccessfullyUpdatedRoutines',
    label: 'Update your routines',
    helper: 'Organize your products into morning and night routines',
    path: '/skincarecabinet',
    triggerHighlight: true,
  },
  {
    stepIndex: 3,
    key: 'hasVisitedAMRoutine',
    label: 'Review morning routine',
    helper: 'Confirm your AM routine looks right',
    path: '/routines/morning',
  },
  {
    stepIndex: 4,
    key: 'hasVisitedPMRoutine',
    label: 'Review nighttime routine',
    helper: 'Confirm your PM routine looks right',
    path: '/routines/night',
  },
] as const;

function ProgressRing({ percent, size = 44 }: { percent: number; size?: number }) {
  const stroke = 3;
  const radius = (size - stroke) / 2;
  const circumference = 2 * Math.PI * radius;
  const offset = circumference - (percent / 100) * circumference;

  return (
    <svg
      width={size}
      height={size}
      className="routine-setup-checklist-progress-ring"
      aria-hidden
    >
      <circle
        cx={size / 2}
        cy={size / 2}
        r={radius}
        fill="none"
        stroke="rgba(255, 255, 255, 0.35)"
        strokeWidth={stroke}
      />
      <circle
        cx={size / 2}
        cy={size / 2}
        r={radius}
        fill="none"
        stroke="var(--text-on-accent)"
        strokeWidth={stroke}
        strokeDasharray={circumference}
        strokeDashoffset={offset}
        strokeLinecap="round"
        transform={`rotate(-90 ${size / 2} ${size / 2})`}
      />
      <text
        x="50%"
        y="50%"
        textAnchor="middle"
        dominantBaseline="central"
        className="routine-setup-checklist-progress-text"
      >
        {percent}%
      </text>
    </svg>
  );
}

const RoutineSetupChecklist: React.FC = () => {
  const { progress, dismissChecklist } = useRoutineSetupChecklist();
  const { user } = useAuth();
  const navigate = useNavigate();

  const firstName = useMemo(() => {
    if (user?.name) return user.name.split(' ')[0];
    if (user?.email) return user.email.split('@')[0];
    return 'there';
  }, [user]);

  const getStepCompleted = (key: (typeof STEPS)[number]['key']) => !!progress[key];

  const getFirstIncompleteIndex = () => {
    for (let i = 0; i < STEPS.length; i++) {
      if (!getStepCompleted(STEPS[i].key)) return i;
    }
    return STEPS.length;
  };

  const firstIncompleteIndex = getFirstIncompleteIndex();
  const completedCount = STEPS.filter((step) => getStepCompleted(step.key)).length;
  const progressPercent = Math.round((completedCount / STEPS.length) * 100);

  const handleStepClick = (step: (typeof STEPS)[number], index: number) => {
    if (index !== firstIncompleteIndex) return;
    if (step.openAddProduct) {
      navigate('/skincarecabinet?openAddProduct=1');
      return;
    }
    if (step.triggerHighlight) {
      navigate('/skincarecabinet?highlightUpdateRoutines=1');
      return;
    }
    navigate(step.path);
  };

  return (
    <div className="routine-setup-checklist routine-setup-checklist-card">
      <div className="routine-setup-checklist-header">
        <ProgressRing percent={progressPercent} />
        <div className="routine-setup-checklist-header-copy">
          <Typography variant="body-small-bold" className="routine-setup-checklist-header-title">
            Keep it up, {firstName}!
          </Typography>
          <Typography variant="body-small" className="routine-setup-checklist-header-subtitle">
            Complete the steps below to get familiar with Dew.
          </Typography>
        </div>
        <button
          type="button"
          className="routine-setup-checklist-close"
          onClick={dismissChecklist}
          aria-label="Close getting started tour"
        >
          <Icon name="close" size="small" colorVariant="white" />
        </button>
      </div>

      <ol className="routine-setup-checklist-steps" aria-label="Getting started steps">
        {STEPS.map((step, index) => {
          const completed = getStepCompleted(step.key);
          const isActive = index === firstIncompleteIndex;
          const isPending = !completed && !isActive;

          return (
            <li
              key={step.key}
              className={[
                'routine-setup-checklist-step',
                completed ? 'completed' : '',
                isActive ? 'active' : '',
                isPending ? 'pending' : '',
              ]
                .filter(Boolean)
                .join(' ')}
            >
              <button
                type="button"
                className="routine-setup-checklist-step-button"
                onClick={() => handleStepClick(step, index)}
                disabled={!isActive}
                aria-current={isActive ? 'step' : undefined}
              >
                <span className="routine-setup-checklist-step-track" aria-hidden>
                  <span className="routine-setup-checklist-step-indicator">
                    {completed ? (
                      <Icon name="check" size="small" colorVariant="white" />
                    ) : null}
                  </span>
                  {index < STEPS.length - 1 ? (
                    <span className="routine-setup-checklist-step-connector" />
                  ) : null}
                </span>

                <span className="routine-setup-checklist-step-content">
                  <Typography variant="body-small" className="routine-setup-checklist-step-label">
                    {step.label}
                  </Typography>
                  {isActive ? (
                    <Typography variant="body-small" className="routine-setup-checklist-step-helper">
                      {step.helper}
                    </Typography>
                  ) : null}
                </span>

                {isActive ? (
                  <Icon
                    name="chevron-right"
                    size="small"
                    colorVariant="primary-green"
                    className="routine-setup-checklist-step-chevron"
                  />
                ) : null}
              </button>
            </li>
          );
        })}
      </ol>
    </div>
  );
};

export default RoutineSetupChecklist;
